import Container from "@/components/atoms/Container";
import Button from "@/components/atoms/Button";
import { router } from "@inertiajs/react";
import User from "@/types/User";
import { useState } from "react";
import Permission from "@/types/Permission";
import useAccess from "@/hooks/useAccess";
import Tooltip from "@/components/atoms/Tooltip";
import Input from "@/components/atoms/Input";
import { Switch } from "@/components/atoms/Switch";

// Types
type Props = {
  user: User;
  allPermissions: Permission[];
};

const PermissionsTab = ({ user, allPermissions }: Props) => {
  const { getAccess, permissions, isAdmin } = useAccess();
  const userRolesPermissions = user.roles.map((role) => role.permissions);
  const uniqueUserRolesPermissions = Array.from(
    new Set(userRolesPermissions.map((permission) => JSON.stringify(permission)))
  ).map((permission) => JSON.parse(permission));

  const userPermissions = [
    ...uniqueUserRolesPermissions,
    ...(user.direct_permissions ? user.direct_permissions : []),
  ].flat();

  const uniqueUserPermissions = Array.from(
    new Set(userPermissions.map((permission) => JSON.stringify(permission)))
  ).map((permission) => JSON.parse(permission));

  const [selected, setSelected] = useState<string[]>(uniqueUserPermissions.map((permission) => `${permission.id}`) || []);
  const [searchQuery, setSearchQuery] = useState("");

  const useAblePermissions = isAdmin ? allPermissions : permissions;

  // Group permissions by their controllers
  const permissionGroups: Record<string, Permission[]> = useAblePermissions.reduce<Record<string, Permission[]>>(
    (acc, permission) => {
      if (!acc[permission.controller]) {
        acc[permission.controller] = [];
      }
      acc[permission.controller].push(permission);
      return acc;
    },
    {}
  );

  // Handle permission toggling
  const handleCheck = (checked: boolean, id: string) => {
    setSelected(
      checked ? [...selected, id] : selected.filter((select) => select !== id)
    );
  };

  // Handle saving direct permissions
  const handleSave = () => {
    const uniqueUserRolesPermissionsIds = uniqueUserRolesPermissions
      .map((rolePermission) => rolePermission.map((rp: { id: string | number }) => `${rp.id}`))
      .flat();

    router.post(`/admin/users/${user.id}/set_user_permissions`, {
      data: selected.filter((item) => !uniqueUserRolesPermissionsIds.includes(item)),
    });
  };

  // Filter permissions based on search query
  const filteredPermissionGroups = Object.entries(permissionGroups).reduce(
    (acc: Record<string, Permission[]>, [controller, permissions]) => {
      const filteredPermissions = permissions.filter((permission) =>
        permission.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredPermissions.length > 0) {
        acc[controller] = filteredPermissions;
      }
      return acc;
    },
    {}
  );

  return (
    <Container className="py-4">
      {/* Search Bar */}
      <Input
        placeholder="Search permissions..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="mb-4"
      />

      {/* Permissions List Grouped by Category */}
      {Object.entries(filteredPermissionGroups).map(([controller, permissions]) => (
        <div key={controller} className="mb-4">
          <h4 className="font-bold mb-2">{controller}</h4>
          {permissions.map((permission) => (
            <div key={permission.id} className="flex items-center justify-between mb-2">
              <Tooltip content={permission.description}>
                <span>{permission.name}</span>
              </Tooltip>
              <Switch
                checked={selected.includes(`${permission.id}`)}
                onCheckedChange={(checked) => handleCheck(!!checked, `${permission.id}`)}
                disabled={
                  !getAccess(["set_user_permissions"]) ||
                  uniqueUserRolesPermissions.some((rolePermission) =>
                    rolePermission.some((rp: { id: string | number }) => rp.id == permission.id)
                  )
                }
              />
            </div>
          ))}
        </div>
      ))}

      <Button onClick={handleSave} className="mt-4" disabled={!getAccess(["set_user_permissions"]) || selected.length === 0}>
        Save
      </Button>
    </Container>
  );
};

export default PermissionsTab;
